import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ensureOnlineListener, setReplyQueueQueryClient } from './lib/replyQueue';
import { retryTransient, retryDelayBackoff } from './lib/api';
import { bootAccent } from './lib/accent';
import { bootAppIcon } from './lib/appIcon';
import { wireSwUpdateLifecycle } from './lib/swUpdate';
import './index.css';

// Wire the offline reply queue's online listener once at module init.
// Drains any pending replies from a previous offline session immediately
// if we're online, then waits for future 'online' events.
ensureOnlineListener();

// Apply the owner's chosen accent color + app icon variant before first
// paint so the UI doesn't flash to defaults and back. Both no-op safely
// when localStorage is unavailable (private mode).
bootAccent();
bootAppIcon();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 2026-06-29 (NotificationClick502) — was `retry: 1`. A GHL 429/5xx (worst
      // case: a notification cold-load into a throttled GHL) dead-ended the
      // conversation screen because one in-place refetch just re-hit the same
      // throttle. Now: retry transient errors (429/5xx/network) up to 3× with
      // 1→2→4→8s backoff so the app self-recovers; never retry a 4xx or an auth
      // error. See retryTransient/retryDelayBackoff in lib/api.
      retry: retryTransient,
      retryDelay: retryDelayBackoff,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

// 2026-06-02 (cowork cache-coherence patch) — inject the QueryClient into
// replyQueue so drainReplyQueue can invalidate ['conversations', slug]
// after each successful offline-queued send. Without this, offline replies
// leave the Drip pill stale on the row until the 15s staleTime tick.
setReplyQueueQueryClient(queryClient);

// Register the service worker for installability — production only so dev
// HMR isn't intercepted by a stale SW.
// 2026-07-14 (PWA_SelfUpdate) — registration now lives inside
// wireSwUpdateLifecycle, which also POLLS for updates (foreground/focus +
// every 20 min) and applies a new version with exactly-once reload semantics
// that can never interrupt an in-progress reply (see lib/swUpdate.ts).
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    wireSwUpdateLifecycle();
  });
}

// 2026-06-29 (NotificationClick502) — Safari fallback for the notification tap.
// When the SW can't `client.navigate()` (Safari), it postMessages
// { type: 'ss:navigate', url } instead. Without a listener that tap focused a
// stale window and did nothing — forcing a manual hunt for the conversation.
// Hard-navigate to the deep link so a tap always lands on the right thread.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    const data = event.data as { type?: string; url?: string } | null;
    if (data && data.type === 'ss:navigate' && typeof data.url === 'string' && data.url) {
      window.location.assign(data.url);
    }
  });
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element not found');

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
